import React from 'react';

import {assertAndContinue} from 'Asserts';
import AnalyticsParameterConfigModal from 'insights/parameters/AnalyticsParameterConfigModal';
import AnalyticsParameterValueModal from 'insights/parameters/AnalyticsParameterValueModal';
import ParameterPill, {ParameterPillPartTooltipWrapper} from 'insights/parameters/ParameterPill';
import ValueJsonViewer from 'insights/parameters/ValueJsonViewer';
import {useWritableContext} from 'insights/WritableContext';
import legacyRunModal from 'ui-library/legacyRunModal';
import {JSONValue} from 'util/Types';

import {AnalyticsParameterConfigGraphQL} from '../../../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';

export interface DashboardDetailsParameterPillProps {
  config: AnalyticsParameterConfigGraphQL;
  areQueriesRunning: boolean;
  onChangeConfig: (config: AnalyticsParameterConfigGraphQL) => void;
  onChangeValueJson: (configId: string, valueJson: JSONValue) => void;
  valueJson: JSONValue;
}

/**
 * Component to edit a parameter from dashboard details. It wraps ParameterPill and calls the
 * AnalyticsParameter[Config/Value]Modal on click to edit those values.
 */
export default function DashboardDetailsParameterPill(
  props: DashboardDetailsParameterPillProps
): JSX.Element {
  const configWritable = useWritableContext();

  async function handleEditConfig(): Promise<void> {
    if (!assertAndContinue(configWritable, 'Should not be able to edit config if dashboard is read-only')) {
      return;
    }

    const result: AnalyticsParameterConfigGraphQL | null = await legacyRunModal(
      AnalyticsParameterConfigModal,
      {
        initialConfig: props.config,
        initialValueJson: props.valueJson,
        disabled: props.areQueriesRunning,
      }
    );

    if (result == null || props.areQueriesRunning) {
      return;
    }
    props.onChangeConfig(result);
  }

  async function handleEditValue(): Promise<void> {
    if (
      !assertAndContinue(
        !props.areQueriesRunning,
        'Should not be able to edit parameter value while queries are running'
      )
    ) {
      return;
    }

    const result = await legacyRunModal(AnalyticsParameterValueModal, {
      config: props.config,
      initialValueJson: props.valueJson,
    });
    if (result == null) {
      // User closed the modal
      return;
    }
    // User submitted the modal
    props.onChangeValueJson(props.config.id, result.valueJson);
  }

  return (
    <ParameterPill
      renderedConfig={
        <ParameterPillPartTooltipWrapper
          tooltipProps={{
            disabled: !configWritable,
            title: props.areQueriesRunning ? 'View parameter configuration' : 'Configure parameter',
          }}
        >
          {props.config.systemName}
        </ParameterPillPartTooltipWrapper>
      }
      renderedValue={
        <ValueJsonViewer
          areQueriesRunning={props.areQueriesRunning}
          config={props.config}
          valueJson={props.valueJson}
        />
      }
      onClickConfig={configWritable ? handleEditConfig : undefined}
      onClickValue={props.areQueriesRunning ? undefined : handleEditValue}
    />
  );
}
